import { create } from 'zustand';

import type { AnyGatewayEnv } from '../@types';
import type { StripeElementLocale } from '@stripe/stripe-js';

type State = {
  env: AnyGatewayEnv;
  lang: StripeElementLocale | string;
  propertyId: number | string | undefined;
  apiKey: string | undefined;
};

type Action = {
  setLang: (lang: StripeElementLocale | string) => void;
  setPropertyId: (propertyId: number | string) => void;
  setApiKey: (apiKey: string) => void;
  setEnv: (env: AnyGatewayEnv) => void;
  initialize: (props: Partial<State>) => void;
};

export type GlobalStoreType = State & Action;

/**
 * @description Zustand global store for managing environment, language, property, and API key.
 * Provides setter actions and an initialize method for partial state updates.
 *
 * @returns {GlobalStoreType} Zustand store hook for global state and actions.
 */
export const useGlobalStore = create<State & Action>()(set => ({
  env: 'prod',
  lang: 'en' as StripeElementLocale,
  propertyId: undefined,
  apiKey: undefined,
  /**
   * @description Set the current environment.
   * @param {AnyGatewayEnv} env - The environment to set.
   */
  setEnv: (env: AnyGatewayEnv) => set(() => ({ env })),
  /**
   * @description Set the current language.
   * @param {StripeElementLocale | string} lang - The language to set.
   */
  setLang: (lang: StripeElementLocale | string) => set(() => ({ lang })),
  /**
   * @description Set the current property ID.
   * @param {number | string} propertyId - The property ID to set.
   */
  setPropertyId: (propertyId: number | string) => set(() => ({ propertyId })),
  /**
   * @description Set the current API key.
   * @param {string} apiKey - The API key to set.
   */
  setApiKey: (apiKey: string) => set(() => ({ apiKey })),
  /**
   * @description Initialize the store with a partial state.
   * @param {Partial<State>} props - Partial state to initialize.
   */
  initialize: (props: Partial<State>) => set(() => ({ ...props })),
}));
